import { Link } from "react-router-dom";
import { rankToday } from "@/mocks/rankings";
import { topics } from "@/mocks/topics";
import type { Topic } from "@/types/content";
import { formatHeat } from "@/utils/format";
import Reveal from "@/components/base/Reveal";

const findTopic = (id: string): Topic | undefined => topics.find((t) => t.id === id);

export default function HotRankPreview() {
  const list = rankToday
    .map((r) => ({ ...r, topic: findTopic(r.topicId) }))
    .filter((r): r is typeof r & { topic: Topic } => Boolean(r.topic));
  const top = list[0];

  return (
    <section id="hot" className="border-b border-background-200/60 bg-background-100/60 py-14 md:py-20">
      <div className="mx-auto max-w-content px-4 md:px-6">
        <Reveal>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="flex items-center gap-2 text-xs tracking-[0.3em] text-primary-500">
                <i className="ri-fire-fill" />
                TONIGHT HOT
              </span>
              <h2 className="heading-serif mt-3 text-2xl font-black text-foreground-950 md:text-3xl">
                今日热榜 · 此刻正在刷屏的瓜
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-500">
                按浏览量、评论数、点赞密度与讨论增速综合计算，越靠近当下发生的讨论权重越高。
              </p>
            </div>
            <Link
              to="/rank"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500"
            >
              查看完整热榜
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {top && (
            <Reveal className="lg:col-span-5" delay={60}>
              <Link
                to={`/topics/${top.topic.id}`}
                className="group relative flex h-full min-h-[380px] flex-col justify-end overflow-hidden rounded-lg border border-background-200/80"
              >
                <img
                  src={top.topic.cover}
                  alt={`${top.topic.title} - 深夜吃瓜热榜第一话题`}
                  title={`${top.topic.title} - 深夜吃瓜话题讨论`}
                  className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/60 to-background-950/10" />
                <div className="relative z-10 p-5 md:p-7">
                  <span className="brand-font inline-flex h-9 min-w-9 items-center justify-center rounded-md bg-primary-500 px-2 text-lg text-background-50 dark:text-foreground-950">
                    NO.1
                  </span>
                  <h3 className="heading-serif mt-4 text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                    {top.topic.title}
                  </h3>
                  <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground-300">
                    {top.topic.summary}
                  </p>
                  <div className="mt-4 flex items-center gap-4 text-xs text-foreground-300">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-fire-line text-primary-400" />
                      热度 {formatHeat(top.heat)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-chat-3-line text-secondary-400" />
                      {top.topic.comments} 条讨论
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="lg:col-span-7">
            <ol className="flex flex-col gap-2.5">
              {list.slice(1, 7).map((item, index) => (
                <Reveal key={item.topicId} delay={80 + index * 40}>
                  <li>
                    <Link
                      to={`/topics/${item.topic.id}`}
                      className="group flex items-center gap-4 rounded-lg border border-background-200/70 bg-background-50 px-4 py-3.5 transition-all duration-300 hover:border-primary-500/50 hover:bg-background-100"
                    >
                      <span className="brand-font flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-200 text-base text-foreground-700 transition-colors group-hover:bg-secondary-500 group-hover:text-background-50 dark:group-hover:text-foreground-950">
                        {index + 2}
                      </span>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-sm font-medium text-foreground-950 transition-colors group-hover:text-primary-500 md:text-base">
                          {item.topic.title}
                        </h3>
                        <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                          <span className="rounded-full bg-secondary-100/80 px-2 py-0.5 text-secondary-900">
                            {item.topic.categoryName}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-fire-line text-primary-400" />
                            {formatHeat(item.heat)}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-chat-3-line" />
                            {item.topic.comments}
                          </span>
                        </div>
                      </div>
                      <span
                        className={`flex shrink-0 items-center gap-0.5 text-xs ${
                          item.change >= 0 ? "text-primary-500" : "text-secondary-500"
                        }`}
                      >
                        <i className={item.change >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
                        {Math.abs(item.change)}
                      </span>
                    </Link>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}